import { Component, Input } from "@angular/core";
import { NotificationService } from "../../../core/services/notification.service";
import { ExercisesControllerService } from "../../../core/api/controllers/exercises-controller/exercises-controller.service";
import { Router } from "@angular/router";
import { VideoPlayerComponent } from "../../../shared/components/video-player/video-player.component";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationDialogComponent } from "../../../shared/components/confirmation-dialog/confirmation-dialog.component";

@Component({
  selector: "app-category-exercises",
  templateUrl: "./category-exercises.component.html",
  styleUrls: ["./category-exercises.component.scss"],
})
export class CategoryExercisesComponent {
  @Input() exercises: any;
  @Input() exercisesSelected: any;
  @Input() category: any;
  @Input() searchQuery = "";

  private readonly exerciseTypeLabels = [
    "Count",
    "React",
    "Sequence",
    "Agility",
    "Vertical",
    "Companion",
  ];

  private readonly bodyPartLabels = [
    "Left leg",
    "Right leg",
    "Both legs",
    "Left arm",
    "Right arm",
    "Both arms",
  ];

  private readonly exerciseTypeArtwork = [
    "assets/svg/count-exercise.svg",
    "assets/svg/react-exercise.svg",
    "assets/svg/sequence-exercise.svg",
    "assets/svg/agility-exercise.svg",
    "assets/svg/vertical-exercise.svg",
    "assets/svg/companion-exercise.svg",
  ];

  constructor(
    private exerciseService: ExercisesControllerService,
    private notificationService: NotificationService,
    private router: Router,
    private dialog: MatDialog,
  ) {}

  onUpdateExerciseVideo(ex) {
    const exercise = this.getExerciseProps(ex);
    this.exerciseService.updateExerciseVideo(exercise.id).subscribe(
      () => {
        this.notificationService.success(
          "Video will be updated in a few moments",
        );
      },
      (error) => this.notificationService.error(error),
    );
  }

  getExerciseProps(exercise) {
    return {
      ...(exercise.regularExercise || exercise.pairedExercise),
      categoryId: exercise.categoryId,
    };
  }

  updateSelectedExercise(ex) {
    const exercise = this.getExerciseProps(ex);
    const intersection = this.exercisesSelected.find(
      ({ id }) => id === exercise.id,
    );
    if (intersection) {
      this.exercisesSelected.splice(
        this.exercisesSelected.indexOf(intersection),
        1,
      );
    } else {
      this.exercisesSelected.push(exercise);
    }
  }

  isPairedExercise(exercise) {
    return (
      Boolean(exercise.pairedExercise) &&
      Boolean(exercise.pairedExercise.firstExercise) &&
      Boolean(exercise.pairedExercise.secondExercise)
    );
  }

  addToSingleLocation(
    ex: any,
    categoryId: string = null,
    updateVideos = null,
  ): void {
    const queryParams = {};
    const exercise = this.getExerciseProps(ex);
    if (exercise) {
      Object.assign(queryParams, {
        exerciseId: exercise.id,
        exercisePaired: this.isPairedExercise(exercise),
      });
    }

    if (categoryId) {
      const currentSelectedExercises = this.exercisesSelected.filter(
        (item) => item.categoryId === categoryId,
      );
      if (!currentSelectedExercises.length) {
        return;
      }

      Object.assign(queryParams, {
        exerciseId: currentSelectedExercises.map((item) => item.id),
      });
    }

    if (updateVideos) {
      Object.assign(queryParams, { updateVideos });
    }

    this.router.navigate(["/sync-exercise"], { queryParams });
  }

  playVideo(videoUrl: string) {
    this.dialog.open(VideoPlayerComponent, {
      data: {
        toView: videoUrl,
      },
    });
  }

  openDeleteConfirmationDialog(exercise: any) {
    const data = {
      title: "Delete Exercise",
      message: `Are you sure you want to delete the exercise "${exercise?.pairedExercise?.name || exercise?.regularExercise?.name}" ?`,
      cancelButtonLabel: "Cancel",
      confirmButtonLabel: "Delete",
    };
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: data,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.deleteExercise(exercise);
        }
      });
  }

  deleteExercise(exercise: any) {
    const id = exercise?.pairedExercise?.id || exercise?.regularExercise?.id;
    const exercisesBackup = [...this.exercises];
    this.exercises.splice(this.exercises.indexOf(exercise), 1);
    this.exerciseService.removeExercise(id).subscribe(
      () => {
        this.notificationService.success("Exercise deleted successfully.");
      },
      (error) => {
        this.exercises = exercisesBackup;
        this.notificationService.error(error);
      },
    );
  }

  getExerciseName(exercise: any): string {
    return this.getExerciseProps(exercise)?.name || "Untitled exercise";
  }

  getExerciseType(exercise: any): number {
    return this.getExerciseProps(exercise)?.type ?? 0;
  }

  getExerciseTypeLabel(exercise: any): string {
    return (
      this.exerciseTypeLabels[this.getExerciseType(exercise)] || "Exercise"
    );
  }

  getTypeArtwork(exercise: any): string {
    return (
      this.exerciseTypeArtwork[this.getExerciseType(exercise)] ||
      this.exerciseTypeArtwork[0]
    );
  }

  getExerciseImage(exercise: any): string {
    const model = this.getExerciseProps(exercise);
    return (
      model?.thumbnailUrl || model?.imageUrl || this.getTypeArtwork(exercise)
    );
  }

  getComponentImage(component: any, exercise: any): string {
    return (
      component?.thumbnailUrl ||
      component?.imageUrl ||
      this.getTypeArtwork(exercise)
    );
  }

  hasThumbnail(exercise: any): boolean {
    const model = this.getExerciseProps(exercise);
    return Boolean(model?.thumbnailUrl || model?.imageUrl);
  }

  useFallbackImage(event: Event, exercise: any): void {
    const image = event.target as HTMLImageElement;
    const fallback = this.getTypeArtwork(exercise);
    if (image.src.endsWith(fallback)) return;
    image.src = fallback;
    image.classList.add("exercise-card__image--artwork");
  }

  getExerciseDescription(exercise: any): string {
    if (this.isPairedExercise(exercise)) {
      const { firstExercise, secondExercise } = exercise.pairedExercise;
      return `${firstExercise.name} + ${secondExercise.name}`;
    }

    return (
      exercise.regularExercise?.description ||
      "Ready to configure and add to a training session."
    );
  }

  getConfigCount(exercise: any): number {
    if (this.isPairedExercise(exercise)) {
      return exercise.pairedExercise?.pairedExerciseConfigs?.length || 0;
    }

    return exercise.regularExercise?.configs?.length || 0;
  }

  getBodyPartLabel(exercise: any): string {
    if (this.isPairedExercise(exercise)) return "";
    const bodyPart = exercise.regularExercise?.bodyPart;
    return bodyPart === null || bodyPart === undefined
      ? ""
      : this.bodyPartLabels[bodyPart] || "";
  }

  getPrimaryVideoUrl(exercise: any): string {
    if (this.isPairedExercise(exercise)) {
      return (
        exercise.pairedExercise?.firstExercise?.videoUrl ||
        exercise.pairedExercise?.secondExercise?.videoUrl ||
        ""
      );
    }

    return exercise.regularExercise?.videoUrl || "";
  }

  isExerciseSelected(exercise: any): boolean {
    const id = this.getExerciseProps(exercise)?.id;
    return this.exercisesSelected.some((selected) => selected.id === id);
  }

  trackByExerciseId = (_index: number, exercise: any): string =>
    this.getExerciseProps(exercise)?.id;

  get visibleExercises(): any[] {
    const exercises = this.exercises || [];
    const query = this.searchQuery.trim().toLocaleLowerCase();
    if (!query || this.category?.name?.toLocaleLowerCase().includes(query)) {
      return exercises;
    }

    return exercises.filter((exercise) =>
      this.getExerciseName(exercise).toLocaleLowerCase().includes(query),
    );
  }
}
